import type { NextPage } from "next";
import Head from "next/head";
import { useState, ChangeEvent, FormEventHandler } from "react";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import isEmail from "validator/lib/isEmail";
import ErrorMessage from "@components/ErrorMessage";
import Button from "@components/ui/Button";
import Google from "@components/GoogleLogo";
import { useVendorContext } from "@contexts/vendor-context";
import { datadogRum } from "@datadog/browser-rum";
import { cleanEmail } from "@lib/clean-email";
import Icon from "@components/ui/Icon";
import SocialButton from "@components/ui/SocialButton";
import { createOauthQuerystringParams } from "@lib/oauth-query-string-keys";
import Layout from "@components/Layout";
import cn from "classnames";
import { datadogLogs } from "@datadog/browser-logs";
import {
  getSendParamFromAuth0ClientHeader,
  sendParam,
} from "@lib/send-code-link";
import { fetchIsExistingBreakitCustomerByEmail } from "@lib/breakit-helpers";
import { webAuth } from "../auth0/auth0";
import VippsLogo from "../components/VippsLogo";
import cookies from "js-cookie";
import {
  getPasswordLoginSelectionCookieName,
  SesamyPasswordLoginSelection,
} from "@lib/auth-cookies";

function handleSendCode(email: string, state: string, send: sendParam) {
  datadogLogs.logger.info("Sending code to user", { email });

  webAuth.passwordlessStart(
    {
      connection: "email",
      send,
      email,
      authParams: {
        state,
      },
    },
    function (err) {
      if (!err) {
        datadogLogs.logger.info("Code sent to user");
      } else {
        datadogLogs.logger.error("Error sending code to user", { email, err });
      }
    },
  );
}

const EnterEmail: NextPage = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const [email, setEmail] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [touched, setTouched] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { vendorSettings } = useVendorContext();
  // TODO - use these straight from vendor settings - do on another PR
  const { vendor_id: vendorId, client_id: clientId } = router.query;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value: rawValue } = e.target;

    const value = rawValue.trim();

    setEmail(value);

    if (touched) {
      if (!isEmail(value)) {
        setError(t("invalid-email")!);
        datadogLogs.logger.info("Invalid email entered", { value });
      } else {
        setError("");
      }
    }
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = async () => {
    setError("");
    setTouched(true);

    const emailCleaned = cleanEmail(email);

    if (!isEmail(emailCleaned)) {
      setError(t("invalid-email")!);
      return;
    }

    if (emailCleaned !== email) {
      datadogLogs.logger.info("Email has been cleaned -", {
        email,
        emailCleaned,
      });
    }

    setIsLoading(true);
    datadogLogs.logger.info("Email entered", {
      email: emailCleaned,
    });

    if (process.env.NEXT_PUBLIC_VERCEL_ENV === "production") {
      datadogRum.setUser({
        email: emailCleaned,
      });
      datadogLogs.setUser({
        email: emailCleaned,
      });
    }

    if (isBreakit) {
      const isExistingBreakitCustomer =
        await fetchIsExistingBreakitCustomerByEmail(emailCleaned);

      if (!isExistingBreakitCustomer) {
        setError(t("user_account_does_not_exist"));
        datadogLogs.logger.info("breakit - user account does not exist");

        setIsLoading(false);
        return;
      }
    }

    const state = createOauthQuerystringParams(router.query);

    const send = getSendParamFromAuth0ClientHeader(router);

    const sesamyPasswordLoginSelection = cookies.get(
      getPasswordLoginSelectionCookieName(vendorId),
    );

    if (
      sesamyPasswordLoginSelection === SesamyPasswordLoginSelection.password
    ) {
      router.push(
        `/enter-password?email=${encodeURIComponent(emailCleaned)}&${state}`,
      );
    } else {
      await handleSendCode(emailCleaned, state, send);

      router.push(
        `/enter-code?email=${encodeURIComponent(emailCleaned)}&${state}`,
      );
    }
  };

  const pageTitle =
    vendorSettings?.name === "sesamy" ? t("sign_in") : t("welcome");

  const sendType = getSendParamFromAuth0ClientHeader(router);

  const loginDescriptionText =
    sendType === "code"
      ? t("login_description_code")
      : t("login_description_link");

  const isFokus = vendorId === "fokus";
  const isBreakit = vendorId === "breakit";
  const isParcFerme = clientId === "parcferme";
  const showSocialButtons = !isFokus;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <Layout>
        <div className="mb-4 text-lg font-medium sm:text-2xl">{pageTitle}</div>
        <div className="mb-8 text-gray-300">{loginDescriptionText}</div>
        <div className="flex flex-1 flex-col justify-center">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSubmit(e);
            }}
            className="mb-7"
          >
            <input
              autoFocus
              type="text"
              name="email"
              value={email}
              onChange={handleChange}
              placeholder={t("email_placeholder") || ""}
              className={cn(
                "mb-2 w-full rounded-lg border bg-gray-100 px-4 py-5 text-base placeholder:text-gray-300 dark:bg-gray-600 md:text-base",
                {
                  "border-red": error,
                  "border-gray-100 dark:border-gray-500": !error,
                },
              )}
            />
            {!!error && <ErrorMessage>{error}</ErrorMessage>}

            <Button
              className="text-base sm:mt-4 md:text-base"
              type="submit"
              isLoading={isLoading}
              disabled={isLoading}
            >
              <div className="flex items-center space-x-2">
                <span>{t("continue")}</span>
                <Icon className="text-xs" name="arrow-right" />
              </div>
            </Button>
          </form>
          {showSocialButtons && (
            <>
              <div className="relative mb-5 block text-center text-gray-300 dark:text-gray-300">
                <div className="absolute left-0 right-0 top-1/2 border-b border-gray-200 dark:border-gray-600" />
                <div className="relative inline-block bg-white px-2 dark:bg-gray-800">
                  {t("continue_social_login")}
                </div>
              </div>

              <div className="flex space-x-4 sm:flex-col sm:space-x-0 sm:space-y-4 short:flex-row short:space-x-4 short:space-y-0">
                <SocialButton
                  social="facebook"
                  text={t("continue_with", { provider: "Facebook" })}
                  canResize={true}
                  icon={
                    <Icon
                      className="text-xl text-[#1196F5] sm:absolute sm:left-4 sm:top-1/2 sm:-translate-y-1/2 sm:text-2xl short:static short:left-auto short:top-auto short:translate-y-0 short:text-xl"
                      name="facebook"
                    />
                  }
                />
                <SocialButton
                  social="google"
                  text={t("continue_with", { provider: "Google" })}
                  canResize={true}
                  icon={
                    <Google className="h-5 w-5 sm:absolute sm:left-4 sm:top-1/2 sm:h-6 sm:w-6 sm:-translate-y-1/2 short:static short:left-auto short:top-auto short:h-5 short:w-5 short:translate-y-0" />
                  }
                />
                <SocialButton
                  social="apple"
                  text={t("continue_with", { provider: "Apple" })}
                  canResize={true}
                  icon={
                    <Icon
                      className="text-xl text-black dark:text-white sm:absolute sm:left-4 sm:top-1/2 sm:-translate-y-1/2 sm:text-2xl short:static short:left-auto short:top-auto short:translate-y-0 short:text-xl"
                      name="apple"
                    />
                  }
                />
                {isParcFerme && (
                  <SocialButton
                    social="vipps"
                    text={t("continue_with", { provider: "Vipps" })}
                    canResize={true}
                    icon={
                      <VippsLogo className="h-5 w-5 sm:absolute sm:left-4 sm:top-1/2 sm:h-6 sm:w-6 sm:-translate-y-1/2 short:static short:left-auto short:top-auto short:h-5 short:w-5 short:translate-y-0" />
                    }
                  />
                )}
              </div>
            </>
          )}
        </div>
      </Layout>
    </>
  );
};

export default EnterEmail;
